"use client";

import { SharedOutingMapReview } from "./SharedOutingMapReview";
import dynamic from "next/dynamic";
import { isNativeApp, subscribeToNativePlatform } from "@/lib/native/platform";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { acquireRecordingLock, isRecordingWorkspacePath, recordingLabel, recordingReturnHref, retainRecording, type RecordingLockRelease, type RecordingStatus } from "@/lib/companion/recording-runtime";

const NativeWidgetCoordinator = dynamic(() => import("./NativeWidgetCoordinator").then(module => module.NativeWidgetCoordinator), { ssr: false });
const TrackerScreen = dynamic(() => import("./TrackerScreen").then(module => module.TrackerScreen), { ssr: false });
type HostContext = { register: (element: HTMLElement | null) => void; issue: string | null; retry: () => void; active: boolean; showWorkspace: boolean; returnHref: string; status: RecordingStatus | null };
const RecordingContext = createContext<HostContext | null>(null);

/** Lives in SiteShell, not the route. Only opening recording setup loads the recorder. */
export function RecordingHost({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const native = useSyncExternalStore(subscribeToNativePlatform, isNativeApp, () => false);
  const [surface, setSurface] = useState<HTMLElement | null>(null);
  const [portal, setPortal] = useState<HTMLElement | null>(null);
  const [entry, setEntry] = useState<string | null>(null);
  const [status, setStatus] = useState<RecordingStatus | null>(null);
  const [issue, setIssue] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const releaseRef = useRef<RecordingLockRelease | null>(null);
  const ownershipRef = useRef<Promise<RecordingLockRelease | null> | null>(null);
  const active = retainRecording(status);
  const register = useCallback((element: HTMLElement | null) => {
    if (element) setPortal(current => current ?? document.createElement("div"));
    setSurface(element);
  }, []);
  const retry = useCallback(() => { setIssue(null); setAttempt(value => value + 1); }, []);
  const trackPage = pathname === "/companion/track";
  const showWorkspace = active || status?.mode === "story";
  const recordingPage = trackPage || (showWorkspace && isRecordingWorkspacePath(pathname));

  // Moving one portal container preserves the map, camera and sheet state.
  // A detached container keeps sensors alive without exposing hidden controls.
  useLayoutEffect(() => {
    if (!portal || !surface || !recordingPage) return;
    surface.appendChild(portal);
    return () => { portal.remove(); };
  }, [portal, surface, recordingPage]);

  useEffect(() => {
    if (!trackPage || entry !== null) return;
    let cancelled = false;
    const ownership = navigator.locks ? acquireRecordingLock(navigator.locks, ownershipRef.current)
      : Promise.reject(new Error("unsupported"));
    ownershipRef.current = ownership;
    void ownership.then(release => {
      if (cancelled) { void release?.().catch(() => undefined); return; }
      if (!release) {
        setIssue("Recording is open in another PeakWave tab. Finish there or close that tab, then try again here.");
        return;
      }
      releaseRef.current = release;
      setIssue(null);
      setEntry(window.location.search);
    }).catch((error: unknown) => {
      if (!cancelled) setIssue(error instanceof Error && error.message === "unsupported"
        ? "This browser cannot safely coordinate recordings across tabs. Open PeakWave in an up-to-date browser to record. Your saved sessions are unchanged."
        : "Recording ownership could not be checked. Try again before starting a session.");
    });
    return () => { cancelled = true; };
  }, [trackPage, entry, attempt]);

  useEffect(() => {
    if (entry !== null) return;
    void releaseRef.current?.().catch(() => undefined);
    releaseRef.current = null;
  }, [entry]);

  useEffect(() => () => { void releaseRef.current?.().catch(() => undefined); }, []);

  // Discard only idle presentation state when leaving setup or a finished story.
  // Sensor/recovery states stay mounted independently of the visible route.
  if (entry !== null && !recordingPage && !active && status && status.mode !== "checking") {
    setEntry(null);
    setStatus(null);
  }

  return <RecordingContext.Provider value={{ register, issue, retry, active, showWorkspace, returnHref: recordingReturnHref(entry ?? ""), status }}>
    {children}
    {native && pathname !== "/embed/conditions" && <NativeWidgetCoordinator recording={status} returnHref={recordingReturnHref(entry ?? "")} />}
    {entry !== null && portal && <TrackerScreen surface={portal} entrySearch={entry} onStatus={setStatus} />}
    {active && status && !recordingPage && pathname !== "/embed/conditions" && <>
      <div className="h-24" aria-hidden="true" />
      <Link href={recordingReturnHref(entry ?? "")} data-recording-resume className={`fixed inset-x-3 z-50 flex min-h-[60px] items-center justify-between gap-3 rounded-2xl bg-navy px-4 py-3 text-white shadow-lg min-[760px]:left-auto min-[760px]:w-80 ${pathname.startsWith("/companion") ? "bottom-24 min-[760px]:bottom-5" : "bottom-5"}`}>
        <span><span className="block text-sm font-semibold">{recordingLabel(status)}</span><span className="mt-1 block text-xs text-white/75">{Math.floor(status.elapsedS / 60)} min · {status.distanceKm.toFixed(1)} km</span></span>
        <span className="text-sm font-semibold text-teal-light">Open session</span>
      </Link>
    </>}
  </RecordingContext.Provider>;
}

export function useActiveRecording() {
  const host = useContext(RecordingContext);
  return { active: host?.active ?? false, showWorkspace: host?.showWorkspace ?? false, returnHref: host?.returnHref ?? "/companion/track" };
}

/** Read-only status. This hook never mounts or starts the recorder. */
export function useRecordingSummarySource() {
  const host = useContext(RecordingContext);
  return { status: host?.status ?? null, returnHref: host?.returnHref ?? "/companion/track" };
}

/** Planning context belongs to setup; the retained session is the active workspace. */
export function RecordingSetupContext({ children }: { children: ReactNode }) {
  const { showWorkspace } = useActiveRecording();
  return showWorkspace ? null : <>{children}</>;
}

export function RecordingSurface() {
  const host = useContext(RecordingContext);
  if (!host) return <p role="alert">Recording is unavailable. Reload this page to try again.</p>;
  const { issue, retry, register } = host;
  return <>
    <SharedOutingMapReview />
    {issue && <section role="alert" className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-5">
      <h1 className="font-heading text-xl font-bold text-navy">Check your recording</h1>
      <p className="mt-2 text-sm text-text-body">{issue}</p>
      <button type="button" className="mt-3 min-h-11 rounded-sm bg-navy px-4 text-sm font-semibold text-white" onClick={retry}>Try again</button>
    </section>}
    <div ref={register} data-recording-surface />
  </>;
}
